import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Switch,
  Divider,
} from "@mui/material";
import { Edit, Close, DeleteOutline, Build, Add } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import {
  aiToolsService,
  AiToolSource,
  AiToolDefinition,
  AiToolField,
  AiToolFilter,
  OPERATOR_LABELS,
} from "../services/aiToolsService";

const SENSITIVITY: Record<string, { label: string; color: string }> = {
  administrativo: { label: "Administrativo", color: "#6c757d" },
  identificacao: { label: "Identificação", color: "#f0ad4e" },
  clinico_sensivel: { label: "Clínico sensível", color: "#dc3545" },
};
const sensitivityLabel = (s: string) => SENSITIVITY[s]?.label ?? s;
const sensitivityColor = (s: string) => SENSITIVITY[s]?.color ?? "#6c757d";

// Chave sugerida a partir do rótulo (só o que a Anthropic aceita: [a-zA-Z0-9_-]).
const slugKey = (label: string) =>
  label.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 64);

interface FormState {
  id: string | null; // null = criação
  key: string;
  label: string;
  description: string;
  sourceKey: string;
  fields: AiToolField[];
  filters: AiToolFilter[];
}

const AiTools: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const entityId = user?.entityId || "";

  const [sources, setSources] = useState<AiToolSource[]>([]);
  const [defs, setDefs] = useState<AiToolDefinition[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AiToolDefinition | null>(null);

  // Estado do construtor de filtro (linha de adição).
  const [filterField, setFilterField] = useState("");
  const [filterOp, setFilterOp] = useState("");
  const [filterValue, setFilterValue] = useState("");

  const load = async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [srcs, list] = await Promise.all([
        aiToolsService.getSources(),
        aiToolsService.getDefinitions(entityId),
      ]);
      setSources(srcs);
      setDefs(list);
    } catch {
      showToast("Erro ao carregar as ferramentas de IA.", "error");
    } finally {
      setLoading(false);
    }
  };

  /** Minusculas e sem acento: quem busca "agenda" espera achar "Agenda". */
  const normalize = (v: string) =>
    v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  // A busca olha o nome E a descricao: quem procura uma ferramenta lembra o que ela
  // consulta, nao o nome que alguem deu a ela.
  const filtered = defs.filter((d) => {
    if (!search.trim()) {
      return true;
    }
    const alvo = normalize(
      [d.label, d.description, d.sourceLabel].filter(Boolean).join(" "),
    );
    return alvo.includes(normalize(search));
  });

  const clearFilters = () => setSearch("");
  const hasFilterChanges = search !== "";

  useEffect(() => {
    document.title = "Clinic4Us - Ferramentas de IA";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedSource = useMemo(
    () => sources.find((s) => s.key === form?.sourceKey),
    [sources, form?.sourceKey]
  );

  const patch = (changes: Partial<FormState>) =>
    setForm((prev) => (prev ? { ...prev, ...changes } : prev));

  const resetFilterRow = () => {
    setFilterField("");
    setFilterOp("");
    setFilterValue("");
  };

  const openCreate = () => {
    resetFilterRow();
    setForm({ id: null, key: "", label: "", description: "", sourceKey: sources[0]?.key || "", fields: [], filters: [] });
  };

  const openEdit = (d: AiToolDefinition) => {
    resetFilterRow();
    setForm({
      id: d.id,
      key: d.key,
      label: d.label,
      description: d.description,
      sourceKey: d.sourceKey,
      fields: d.fields.map((f) => ({ fieldKey: f.key, anonymize: f.anonymize })),
      filters: d.filters.map((f) => ({ ...f })),
    });
  };

  const closeForm = () => {
    setForm(null);
    resetFilterRow();
  };

  // Trocar a fonte (só na criação) zera campos e filtros (pertencem à fonte).
  const changeSource = (sourceKey: string) => {
    patch({ sourceKey, fields: [], filters: [] });
    resetFilterRow();
  };

  const isFieldSelected = (key: string) => !!form?.fields.some((f) => f.fieldKey === key);
  const isAnonymizable = (key: string) => !!selectedSource?.fields.find((f) => f.key === key)?.anonymizable;

  const toggleField = (key: string) =>
    setForm((prev) => {
      if (!prev) return prev;
      const exists = prev.fields.some((f) => f.fieldKey === key);
      return exists
        ? { ...prev, fields: prev.fields.filter((f) => f.fieldKey !== key) }
        // ao marcar, campo de nome já entra anonimizado (padrão seguro).
        : { ...prev, fields: [...prev.fields, { fieldKey: key, anonymize: isAnonymizable(key) }] };
    });

  const setAnonymize = (key: string, value: boolean) =>
    setForm((prev) =>
      prev ? { ...prev, fields: prev.fields.map((f) => (f.fieldKey === key ? { ...f, anonymize: value } : f)) } : prev);

  const addFilter = () => {
    if (!filterField || !filterOp) return;
    setForm((prev) =>
      prev ? { ...prev, filters: [...prev.filters, { fieldKey: filterField, operator: filterOp, value: filterValue.trim() || null }] } : prev);
    resetFilterRow();
  };

  const removeFilter = (idx: number) =>
    setForm((prev) => (prev ? { ...prev, filters: prev.filters.filter((_, i) => i !== idx) } : prev));

  const save = async () => {
    if (!form) return;
    if (!form.sourceKey) { showToast("Escolha a fonte de dados.", "error"); return; }
    if (!form.label.trim()) { showToast("Informe o rótulo da ferramenta.", "error"); return; }
    if (!form.description.trim()) { showToast("Informe a descrição (o que a IA deve fazer com ela).", "error"); return; }
    if (form.fields.length === 0) { showToast("Selecione ao menos um campo.", "error"); return; }

    setSaving(true);
    try {
      if (form.id) {
        await aiToolsService.update(form.id, {
          label: form.label.trim(), description: form.description.trim(), fields: form.fields, filters: form.filters,
        });
        showToast("Ferramenta atualizada.", "success");
      } else {
        const key = slugKey(form.key.trim() || form.label);
        await aiToolsService.create(entityId, {
          key, label: form.label.trim(), description: form.description.trim(),
          sourceKey: form.sourceKey, fields: form.fields, filters: form.filters,
        });
        showToast("Ferramenta criada.", "success");
      }
      closeForm();
      await load();
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { description?: string } } })?.response?.data?.description ||
        "Erro ao salvar a ferramenta.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await aiToolsService.remove(deleteTarget.id);
      showToast("Ferramenta excluída.", "success");
      setDeleteTarget(null);
      await load();
    } catch {
      showToast("Erro ao excluir a ferramenta.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Operadores disponíveis para o campo escolhido na linha de filtro.
  const filterOperators = useMemo(
    () => selectedSource?.filters.find((f) => f.fieldKey === filterField)?.operators ?? [],
    [selectedSource, filterField]
  );
  const fieldLabel = (key: string) => selectedSource?.fields.find((f) => f.key === key)?.label ?? key;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: 2, flexWrap: "nowrap" }}>
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="h4" className="c4u-page-header__title">Ferramentas de IA
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">Consultas prontas e seguras que os agentes podem usar. Você escolhe a fonte, os campos que a IA recebe e filtros fixos — a consulta é sempre limitada ao que o perfil do usuário já pode ver.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, justifyContent: "flex-end" }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca por palavra"
                  placeholder="Nome, descrição ou fonte"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                />
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={openCreate} title="Nova ferramenta" />
              </div>
            </div>

            <ResultCount shown={filtered.length} total={defs.length} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
              <Build sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>Nenhuma ferramenta criada</Typography>
              <Typography variant="body2">Crie a primeira em "Nova ferramenta".</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {filtered.map((d) => (
                <Paper key={d.id} elevation={0} sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: { xs: 1, sm: 2 }, p: 1.5, border: "1px solid #e9ecef", borderRadius: "8px" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontWeight: 600, color: colors.textPrimary, wordBreak: "break-word" }}>{d.label}</Typography>
                      <Chip label={d.sourceLabel} size="small" sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#e9ecef" }} />
                      <Chip label={sensitivityLabel(d.sensitivity)} size="small" sx={{ height: 20, fontSize: "0.7rem", color: "#fff", bgcolor: sensitivityColor(d.sensitivity) }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block", mt: 0.25 }}>{d.description}</Typography>
                    <Typography variant="caption" sx={{ color: "#adb5bd" }}>chave: {d.key} · {d.fields.length} campo(s){d.filters.length ? ` · ${d.filters.length} filtro(s)` : ""}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexShrink: 0 }}>
                    <IconButton size="small" onClick={() => openEdit(d)} title="Editar" sx={{ backgroundColor: "#03B4C6", color: "white", width: 32, height: 32, "&:hover": { backgroundColor: "#029AAB" } }}>
                      <Edit sx={{ fontSize: "1rem" }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeleteTarget(d)} title="Excluir" sx={{ backgroundColor: "#dc3545", color: "white", width: 32, height: 32, "&:hover": { backgroundColor: "#c82333" } }}>
                      <DeleteOutline sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
          </div>
        </Container>
      </Box>

      {/* Modal criar/editar */}
      <Dialog open={form !== null} onClose={closeForm} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {form?.id ? "Editar ferramenta" : "Nova ferramenta"}
          </Typography>
          <IconButton onClick={closeForm} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important", position: "relative" }}>
          <SavingOverlay open={saving} message="Salvando ferramenta..." />
          {form && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Fonte (imutável na edição) */}
              <FormControl size="small" fullWidth disabled={!!form.id}>
                <InputLabel>Fonte de dados</InputLabel>
                <Select label="Fonte de dados" value={form.sourceKey} onChange={(e) => changeSource(e.target.value)}>
                  {sources.map((s) => (<MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>))}
                </Select>
              </FormControl>

              <TextField
                label="Rótulo" value={form.label}
                onChange={(e) => patch({ label: e.target.value })}
                fullWidth size="small" required inputProps={{ maxLength: 100 }} InputLabelProps={{ shrink: true }}
                placeholder="ex.: Faltas do mês"
              />

              <TextField
                label="Descrição (o que a IA faz com ela)" value={form.description}
                onChange={(e) => patch({ description: e.target.value })}
                fullWidth size="small" required multiline minRows={2} inputProps={{ maxLength: 1000 }} InputLabelProps={{ shrink: true }}
                placeholder="ex.: Lista os atendimentos com status de falta num período."
                helperText="É o que o modelo lê para decidir quando usar a ferramenta."
              />

              {!form.id && (
                <Typography variant="caption" sx={{ color: colors.textSecondary, mt: -1 }}>
                  Chave gerada automaticamente do rótulo (sem espaços/acentos). Fonte e chave não mudam depois.
                </Typography>
              )}

              <Divider textAlign="left"><Typography variant="caption" sx={{ color: colors.textSecondary }}>Campos que a IA recebe</Typography></Divider>
              <Typography variant="caption" sx={{ color: colors.textSecondary, mt: -1.5 }}>
                Em campos de nome, "Anonimizar" envia só o primeiro nome + iniciais ao modelo (ex.: "Maria S. S.").
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {(selectedSource?.fields ?? []).map((f) => {
                  const selected = isFieldSelected(f.key);
                  const selField = form.fields.find((x) => x.fieldKey === f.key);
                  return (
                    <Box key={f.key} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                      <FormControlLabel
                        control={<Checkbox checked={selected} onChange={() => toggleField(f.key)} sx={{ p: 0.5, color: colors.primary, "&.Mui-checked": { color: colors.primary } }} />}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <span>{f.label}</span>
                            <Chip label={sensitivityLabel(f.sensitivity)} size="small" sx={{ height: 18, fontSize: "0.65rem", color: "#fff", bgcolor: sensitivityColor(f.sensitivity) }} />
                          </Box>
                        }
                        sx={{ ml: 0, flex: 1, minWidth: 0 }}
                      />
                      {f.anonymizable && selected && (
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={selField?.anonymize ?? false}
                              onChange={(e) => setAnonymize(f.key, e.target.checked)}
                              sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: colors.primary }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: colors.primary } }}
                            />
                          }
                          label={<Typography variant="caption" sx={{ color: colors.textSecondary }}>Anonimizar</Typography>}
                          labelPlacement="start"
                          sx={{ ml: 0, mr: 0 }}
                        />
                      )}
                    </Box>
                  );
                })}
                {selectedSource && selectedSource.fields.length === 0 && (
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>Esta fonte não expõe campos.</Typography>
                )}
              </Box>

              {/* Filtros fixos (só se a fonte permite) */}
              {selectedSource && selectedSource.filters.length > 0 && (
                <>
                  <Divider textAlign="left"><Typography variant="caption" sx={{ color: colors.textSecondary }}>Filtros fixos (opcional)</Typography></Divider>
                  {form.filters.map((flt, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, p: 0.75, border: "1px solid #e9ecef", borderRadius: "6px" }}>
                      <Typography variant="body2" sx={{ flex: 1, color: colors.textPrimary }}>
                        <strong>{fieldLabel(flt.fieldKey)}</strong> {OPERATOR_LABELS[flt.operator] ?? flt.operator} {flt.value ? <em>{flt.value}</em> : ""}
                      </Typography>
                      <IconButton size="small" onClick={() => removeFilter(i)} sx={{ color: colors.error }}><DeleteOutline sx={{ fontSize: "1.1rem" }} /></IconButton>
                    </Box>
                  ))}
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    <FormControl size="small" sx={{ minWidth: 130, flex: "1 1 130px" }}>
                      <InputLabel>Campo</InputLabel>
                      <Select label="Campo" value={filterField} onChange={(e) => { setFilterField(e.target.value); setFilterOp(""); }}>
                        {selectedSource.filters.map((f) => (<MenuItem key={f.fieldKey} value={f.fieldKey}>{fieldLabel(f.fieldKey)}</MenuItem>))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120, flex: "1 1 120px" }} disabled={!filterField}>
                      <InputLabel>Operador</InputLabel>
                      <Select label="Operador" value={filterOp} onChange={(e) => setFilterOp(e.target.value)}>
                        {filterOperators.map((op) => (<MenuItem key={op} value={op}>{OPERATOR_LABELS[op] ?? op}</MenuItem>))}
                      </Select>
                    </FormControl>
                    <TextField size="small" label="Valor" value={filterValue} onChange={(e) => setFilterValue(e.target.value)} sx={{ flex: "1 1 120px" }} inputProps={{ maxLength: 255 }} InputLabelProps={{ shrink: true }} />
                    <Button size="small" startIcon={<Add />} onClick={addFilter} disabled={!filterField || !filterOp} sx={{ textTransform: "none", color: colors.primary }}>Adicionar</Button>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={closeForm} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button onClick={save} variant="contained" disabled={saving} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
            {saving ? "Salvando..." : form?.id ? "Salvar" : "Criar ferramenta"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir ferramenta"
        message={`Excluir a ferramenta "${deleteTarget?.label ?? ""}"? Os agentes deixam de poder usá-la.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default AiTools;
